import {
    IApproveJoinRequest,
    IBulkApproveJoinRequests,
    IBulkRejectJoinRequests,
    IBulkRescindParticipants,
    IBulkRescindResult,
    IBrand,
    IBrandSocialLoginPayload,
    IBrandSocialProfile,
    ICreateBrandSocialLinkPayload,
    IUpdateBrandSocialLinkPayload,
    IBuzzBridgeProfileResponse,
    ICampaign,
    ICampaignWallet,
    ICreateBrand,
    ICreateCampaignPayload,
    IEscrow,
    IEscrowPayload,
    ICampaignWalletDepositSocketPayload,
    IAdminCreditWalletRequest,
    IAdminCreditWalletResult,
    IFundWalletPayload,
    IFundWalletResponse,
    IVerifyWalletFundPayload,
    IGetBuzzBridgeProfile,
    IGetPublicInfluencerProfilePayload,
    ISaveManualInstagramLinkPayload,
    IGetCampaigns,
    IManagedCampaignsListResult,
    IGetHostDashboard,
    IGetTransactionsPayload,
    IHostDashboardData,
    IInfluencer,
    IInviteInfluencer,
    IParticipant,
    IPayoutPayload,
    IRejectJoinRequest,
    ICancelCampaignInvite,
    IGetCampaignPendingInvites,
    IGetCampaignPendingInvitesSuccess,
    IRescindParticipantApproval,
    IRequestToJoinCampaign,
    IUpdateAutoReloadPayload,
    IUpdateCampaignPayload,
    IWalletDashboard,
    IWalletFundOptions,
    IWalletTransaction,
    types,
} from './types';
import { createAction } from '@reduxjs/toolkit';
import { IErrorType } from '../../constants/types';
import type {
    IBoostCampaignReportPayload,
    IBoostCampaignReportResult,
    ICampaignReportContentResponse,
    ICampaignReportCreatorsResponse,
    ICampaignReportSummary,
    ICampaignReportSyncStatusResponse,
    IGetCampaignReportContentPayload,
    IGetCampaignReportCreatorsPayload,
    IGetCampaignReportPayload,
    ISyncCampaignReportPayload,
} from '../../types/global/campaignReport.types';
import type { ICommissionWalletSummary } from '../../types/global/commissionWallet.types';
import { IGenericDocumentModifier } from '../../types/global/helper';
import type { ICampaignApplicationReviewData } from './campaignApplicationReview.types';
import type {
    IGetCampaignWorkspacePayload,
    IGetCampaignWorkspaceSuccessPayload,
    ISubmitParticipantWorkPayload,
    ISubmitLiveUrlPayload,
    IRetrySubmissionProcessingPayload,
    IMarkSharedPayload,
    IRequestLiveUrlRevisionPayload,
} from './campaignWorkspace.types';
import type { ISaveBrandReviewPayload, ISaveBrandReviewSuccessPayload } from './brandReview.types';
import type { ISaveInfluencerReviewPayload, ISaveInfluencerReviewSuccessPayload } from './influencerReview.types';
import type {
    IDeleteCampaignThreadMessagePayload,
    IDeleteCampaignThreadMessageSuccessPayload,
    IListCampaignThreadMessagesPayload,
    IListCampaignThreadMessagesSuccessPayload,
    IPatchCampaignThreadMessagePayload,
    IPatchCampaignThreadMessageSuccessPayload,
    ISendCampaignThreadMessagePayload,
    ISendCampaignThreadMessageSuccessPayload,
    IBroadcastCampaignParticipantMessagePayload,
    IBroadcastCampaignParticipantMessageResult,
    IMarkCampaignThreadReadPayload,
    IMarkCampaignThreadReadSuccessPayload,
    IMarkAllCampaignThreadsReadPayload,
    IMarkAllCampaignThreadsReadSuccessPayload,
    ICampaignThreadSocketJoinPayload,
    ICampaignThreadSocketTypingPayload,
    ICampaignThreadSocketMessagePayload,
    ICampaignThreadSocketDeletedPayload,
    ICampaignThreadAttachmentProcessingPayload,
    ICampaignThreadUnreadUpdatedPayload,
    ICampaignThreadTypingState,
    ICampaignThreadSocketErrorPayload,
} from './campaignThreadMessage.types';

// Program actions
export const joinProgram = createAction<any>(types.JOIN_PROGRAM);
export const joinProgramSuccess = createAction<any>(types.JOIN_PROGRAM_SUCCESS);
export const joinProgramFail = createAction<IErrorType>(types.JOIN_PROGRAM_FAIL);

// Campaign actions
export const createOffsiteCampaign = createAction<ICreateCampaignPayload>(types.CREATE_OFFSITE_CAMPAIGN);
export const createOffsiteCampaignSuccess = createAction<ICampaign>(types.CREATE_OFFSITE_CAMPAIGN_SUCCESS);
export const createOffsiteCampaignFail = createAction<IErrorType>(types.CREATE_OFFSITE_CAMPAIGN_FAIL);
export const resetCreatingCampaign = createAction(types.RESET_CREATING_CAMPAIGN);

export const requestToJoinCampaign = createAction<IRequestToJoinCampaign>(types.REQUEST_TO_JOIN_CAMPAIGN);
export const requestToJoinCampaignSuccess = createAction<IParticipant>(types.REQUEST_TO_JOIN_CAMPAIGN_SUCCESS);
export const requestToJoinCampaignFail = createAction<IErrorType>(types.REQUEST_TO_JOIN_CAMPAIGN_FAIL);

export const approveCampaignJoinRequest = createAction<IApproveJoinRequest>(types.APPROVE_CAMPAIGN_JOIN_REQUEST);
export const bulkApproveCampaignJoinRequests = createAction<IBulkApproveJoinRequests>(
    types.BULK_APPROVE_CAMPAIGN_JOIN_REQUESTS,
);
export const bulkApproveCampaignJoinRequestsSuccess = createAction<{ approved: number; campaignId: string }>(
    types.BULK_APPROVE_CAMPAIGN_JOIN_REQUESTS_SUCCESS,
);
export const bulkApproveCampaignJoinRequestsFail = createAction<IErrorType>(
    types.BULK_APPROVE_CAMPAIGN_JOIN_REQUESTS_FAIL,
);
export const approveCampaignJoinRequestSuccess = createAction<IParticipant>(
    types.APPROVE_CAMPAIGN_JOIN_REQUEST_SUCCESS,
);
export const approveCampaignJoinRequestFail = createAction<IErrorType>(types.APPROVE_CAMPAIGN_JOIN_REQUEST_FAIL);

export const bulkRejectCampaignJoinRequests = createAction<IBulkRejectJoinRequests>(
    types.BULK_REJECT_CAMPAIGN_JOIN_REQUESTS,
);
export const bulkRejectCampaignJoinRequestsSuccess = createAction<{ rejected: number; campaignId: string }>(
    types.BULK_REJECT_CAMPAIGN_JOIN_REQUESTS_SUCCESS,
);
export const bulkRejectCampaignJoinRequestsFail = createAction<IErrorType>(
    types.BULK_REJECT_CAMPAIGN_JOIN_REQUESTS_FAIL,
);

export const bulkRescindCampaignParticipants = createAction<IBulkRescindParticipants>(
    types.BULK_RESCIND_CAMPAIGN_PARTICIPANTS,
);
export const bulkRescindCampaignParticipantsSuccess = createAction<IBulkRescindResult & { campaignId: string }>(
    types.BULK_RESCIND_CAMPAIGN_PARTICIPANTS_SUCCESS,
);
export const bulkRescindCampaignParticipantsFail = createAction<IErrorType>(
    types.BULK_RESCIND_CAMPAIGN_PARTICIPANTS_FAIL,
);

export const rejectCampaignJoinRequest = createAction<IRejectJoinRequest>(types.REJECT_CAMPAIGN_JOIN_REQUEST);
export const rejectCampaignJoinRequestSuccess = createAction<IParticipant>(types.REJECT_CAMPAIGN_JOIN_REQUEST_SUCCESS);
export const rejectCampaignJoinRequestFail = createAction<IErrorType>(types.REJECT_CAMPAIGN_JOIN_REQUEST_FAIL);

export const getCampaignPendingInvites = createAction<IGetCampaignPendingInvites>(types.GET_CAMPAIGN_PENDING_INVITES);
export const getCampaignPendingInvitesSuccess = createAction<IGetCampaignPendingInvitesSuccess>(
    types.GET_CAMPAIGN_PENDING_INVITES_SUCCESS,
);
export const getCampaignPendingInvitesFail = createAction<IErrorType>(types.GET_CAMPAIGN_PENDING_INVITES_FAIL);

export const cancelCampaignInvite = createAction<ICancelCampaignInvite>(types.CANCEL_CAMPAIGN_INVITE);
export const cancelCampaignInviteSuccess = createAction<{ participantId: string; campaignId: string }>(
    types.CANCEL_CAMPAIGN_INVITE_SUCCESS,
);
export const cancelCampaignInviteFail = createAction<IErrorType>(types.CANCEL_CAMPAIGN_INVITE_FAIL);

export const rescindCampaignParticipantApproval = createAction<IRescindParticipantApproval>(
    types.RESCIND_CAMPAIGN_PARTICIPANT_APPROVAL,
);
export const rescindCampaignParticipantApprovalSuccess = createAction<{
    participant: IParticipant;
    participantId: string;
    campaignId: string;
    participants_count?: number;
}>(types.RESCIND_CAMPAIGN_PARTICIPANT_APPROVAL_SUCCESS);
export const rescindCampaignParticipantApprovalFail = createAction<IErrorType>(
    types.RESCIND_CAMPAIGN_PARTICIPANT_APPROVAL_FAIL,
);

export const getOffsiteCampaignDetails = createAction<{
    campaignId: string;
    /** Brand/host dashboard: GET /campaigns/:id (auth). Omit or false: public influencer flow GET /campaigns/public/:id */
    asBrandHost?: boolean;
}>(types.GET_OFFSITE_CAMPAIGN_DETAILS);
export const getOffsiteCampaignDetailsSuccess = createAction<ICampaign>(types.GET_OFFSITE_CAMPAIGN_DETAILS_SUCCESS);
export const getOffsiteCampaignDetailsFail = createAction<IErrorType>(types.GET_OFFSITE_CAMPAIGN_DETAILS_FAIL);

export const getCampaignJobDetails = createAction<{ campaignId: string }>(types.GET_CAMPAIGN_JOB_DETAILS);
export const getCampaignJobDetailsSuccess = createAction<IParticipant[]>(types.GET_CAMPAIGN_JOB_DETAILS_SUCCESS);
export const getCampaignJobDetailsFail = createAction<IErrorType>(types.GET_CAMPAIGN_JOB_DETAILS_FAIL);

export const getCampaignApplicationReview = createAction<{ participantId: string }>(
    types.GET_CAMPAIGN_APPLICATION_REVIEW,
);
export const getCampaignApplicationReviewSuccess = createAction<ICampaignApplicationReviewData>(
    types.GET_CAMPAIGN_APPLICATION_REVIEW_SUCCESS,
);
export const getCampaignApplicationReviewFail = createAction<IErrorType>(types.GET_CAMPAIGN_APPLICATION_REVIEW_FAIL);
export const clearCampaignApplicationReview = createAction(types.CLEAR_CAMPAIGN_APPLICATION_REVIEW);

export const getCampaignWorkspace = createAction<IGetCampaignWorkspacePayload>(types.GET_CAMPAIGN_WORKSPACE);
export const getCampaignWorkspaceSuccess = createAction<IGetCampaignWorkspaceSuccessPayload>(
    types.GET_CAMPAIGN_WORKSPACE_SUCCESS,
);
export const getCampaignWorkspaceFail = createAction<IErrorType>(types.GET_CAMPAIGN_WORKSPACE_FAIL);
export const clearCampaignWorkspace = createAction(types.CLEAR_CAMPAIGN_WORKSPACE);
export const workspaceSubmissionRevisionReceived = createAction<{
    submissionId: string;
    deliverableType: string;
    feedback: string;
}>(types.WORKSPACE_SUBMISSION_REVISION_RECEIVED);

export const sendCampaignThreadMessage = createAction<ISendCampaignThreadMessagePayload>(
    types.SEND_CAMPAIGN_THREAD_MESSAGE,
);
export const sendCampaignThreadMessageSuccess = createAction<ISendCampaignThreadMessageSuccessPayload>(
    types.SEND_CAMPAIGN_THREAD_MESSAGE_SUCCESS,
);
export const sendCampaignThreadMessageFail = createAction<IErrorType>(types.SEND_CAMPAIGN_THREAD_MESSAGE_FAIL);

export const broadcastCampaignParticipantMessage = createAction<IBroadcastCampaignParticipantMessagePayload>(
    types.BROADCAST_CAMPAIGN_PARTICIPANT_MESSAGE,
);
export const broadcastCampaignParticipantMessageSuccess = createAction<IBroadcastCampaignParticipantMessageResult>(
    types.BROADCAST_CAMPAIGN_PARTICIPANT_MESSAGE_SUCCESS,
);
export const broadcastCampaignParticipantMessageFail = createAction<IErrorType>(
    types.BROADCAST_CAMPAIGN_PARTICIPANT_MESSAGE_FAIL,
);

export const listCampaignThreadMessages = createAction<IListCampaignThreadMessagesPayload>(
    types.LIST_CAMPAIGN_THREAD_MESSAGES,
);
export const listCampaignThreadMessagesSuccess = createAction<IListCampaignThreadMessagesSuccessPayload>(
    types.LIST_CAMPAIGN_THREAD_MESSAGES_SUCCESS,
);
export const listCampaignThreadMessagesFail = createAction<IErrorType>(types.LIST_CAMPAIGN_THREAD_MESSAGES_FAIL);

export const patchCampaignThreadMessage = createAction<IPatchCampaignThreadMessagePayload>(
    types.PATCH_CAMPAIGN_THREAD_MESSAGE,
);
export const patchCampaignThreadMessageSuccess = createAction<IPatchCampaignThreadMessageSuccessPayload>(
    types.PATCH_CAMPAIGN_THREAD_MESSAGE_SUCCESS,
);
export const patchCampaignThreadMessageFail = createAction<IErrorType>(types.PATCH_CAMPAIGN_THREAD_MESSAGE_FAIL);

export const deleteCampaignThreadMessage = createAction<IDeleteCampaignThreadMessagePayload>(
    types.DELETE_CAMPAIGN_THREAD_MESSAGE,
);
export const deleteCampaignThreadMessageSuccess = createAction<IDeleteCampaignThreadMessageSuccessPayload>(
    types.DELETE_CAMPAIGN_THREAD_MESSAGE_SUCCESS,
);
export const deleteCampaignThreadMessageFail = createAction<IErrorType>(types.DELETE_CAMPAIGN_THREAD_MESSAGE_FAIL);

export const markCampaignThreadRead = createAction<IMarkCampaignThreadReadPayload>(types.MARK_CAMPAIGN_THREAD_READ);
export const markCampaignThreadReadSuccess = createAction<IMarkCampaignThreadReadSuccessPayload>(
    types.MARK_CAMPAIGN_THREAD_READ_SUCCESS,
);
export const markCampaignThreadReadFail = createAction<IErrorType>(types.MARK_CAMPAIGN_THREAD_READ_FAIL);

export const markAllCampaignThreadsRead = createAction<IMarkAllCampaignThreadsReadPayload>(
    types.MARK_ALL_CAMPAIGN_THREADS_READ,
);
export const markAllCampaignThreadsReadSuccess = createAction<IMarkAllCampaignThreadsReadSuccessPayload>(
    types.MARK_ALL_CAMPAIGN_THREADS_READ_SUCCESS,
);
export const markAllCampaignThreadsReadFail = createAction<IErrorType>(types.MARK_ALL_CAMPAIGN_THREADS_READ_FAIL);

export const campaignThreadSocketJoin = createAction<ICampaignThreadSocketJoinPayload>(
    types.CAMPAIGN_THREAD_SOCKET_JOIN,
);
export const campaignThreadSocketLeave = createAction<ICampaignThreadSocketJoinPayload>(
    types.CAMPAIGN_THREAD_SOCKET_LEAVE,
);
export const campaignThreadSocketTyping = createAction<ICampaignThreadSocketTypingPayload>(
    types.CAMPAIGN_THREAD_SOCKET_TYPING,
);
export const campaignThreadMessageNew = createAction<ICampaignThreadSocketMessagePayload>(
    types.CAMPAIGN_THREAD_MESSAGE_NEW,
);
export const campaignThreadMessageUpdated = createAction<ICampaignThreadSocketMessagePayload>(
    types.CAMPAIGN_THREAD_MESSAGE_UPDATED,
);
export const campaignThreadMessageDeleted = createAction<ICampaignThreadSocketDeletedPayload>(
    types.CAMPAIGN_THREAD_MESSAGE_DELETED,
);
export const campaignThreadAttachmentProcessing = createAction<ICampaignThreadAttachmentProcessingPayload>(
    types.CAMPAIGN_THREAD_ATTACHMENT_PROCESSING,
);
export const campaignThreadUnreadUpdated = createAction<ICampaignThreadUnreadUpdatedPayload>(
    types.CAMPAIGN_THREAD_UNREAD_UPDATED,
);
export const campaignThreadSocketError = createAction<ICampaignThreadSocketErrorPayload>(
    types.CAMPAIGN_THREAD_SOCKET_ERROR,
);
export const campaignThreadTypingReceived = createAction<{
    participantId: string;
    typing: ICampaignThreadTypingState | null;
}>(types.CAMPAIGN_THREAD_TYPING_RECEIVED);

export const submitParticipantWork = createAction<ISubmitParticipantWorkPayload>(types.SUBMIT_PARTICIPANT_WORK);
export const submitParticipantWorkSuccess = createAction<unknown>(types.SUBMIT_PARTICIPANT_WORK_SUCCESS);
export const submitParticipantWorkFail = createAction<IErrorType>(types.SUBMIT_PARTICIPANT_WORK_FAIL);
export const setParticipantUploadProgress = createAction<number>(types.SET_PARTICIPANT_UPLOAD_PROGRESS);

export const submitLiveUrl = createAction<ISubmitLiveUrlPayload>(types.SUBMIT_LIVE_URL);
export const submitLiveUrlSuccess = createAction<unknown>(types.SUBMIT_LIVE_URL_SUCCESS);
export const submitLiveUrlFail = createAction<IErrorType>(types.SUBMIT_LIVE_URL_FAIL);

export const retrySubmissionProcessing = createAction<IRetrySubmissionProcessingPayload>(
    types.RETRY_SUBMISSION_PROCESSING,
);
export const retrySubmissionProcessingSuccess = createAction<unknown>(types.RETRY_SUBMISSION_PROCESSING_SUCCESS);
export const retrySubmissionProcessingFail = createAction<IErrorType>(types.RETRY_SUBMISSION_PROCESSING_FAIL);

export const markShared = createAction<IMarkSharedPayload>(types.MARK_SHARED);
export const markSharedSuccess = createAction<unknown>(types.MARK_SHARED_SUCCESS);
export const markSharedFail = createAction<IErrorType>(types.MARK_SHARED_FAIL);

export const requestLiveUrlRevision = createAction<IRequestLiveUrlRevisionPayload>(types.REQUEST_LIVE_URL_REVISION);
export const requestLiveUrlRevisionSuccess = createAction<unknown>(types.REQUEST_LIVE_URL_REVISION_SUCCESS);
export const requestLiveUrlRevisionFail = createAction<IErrorType>(types.REQUEST_LIVE_URL_REVISION_FAIL);

export const withdrawFromCampaign = createAction<{ participantId: string }>(types.WITHDRAW_FROM_CAMPAIGN);
export const withdrawFromCampaignSuccess = createAction<unknown>(types.WITHDRAW_FROM_CAMPAIGN_SUCCESS);
export const withdrawFromCampaignFail = createAction<IErrorType>(types.WITHDRAW_FROM_CAMPAIGN_FAIL);

export const requestDeliveryExtension = createAction<{
    participantId: string;
    requestedDays: number;
    reason?: string;
    round_id?: string;
}>(types.REQUEST_DELIVERY_EXTENSION);
export const requestDeliveryExtensionSuccess = createAction<unknown>(types.REQUEST_DELIVERY_EXTENSION_SUCCESS);
export const requestDeliveryExtensionFail = createAction<IErrorType>(types.REQUEST_DELIVERY_EXTENSION_FAIL);

export const approveDeliveryExtension = createAction<{
    participantId: string;
    requestId: string;
}>(types.APPROVE_DELIVERY_EXTENSION);
export const approveDeliveryExtensionSuccess = createAction<unknown>(types.APPROVE_DELIVERY_EXTENSION_SUCCESS);
export const approveDeliveryExtensionFail = createAction<IErrorType>(types.APPROVE_DELIVERY_EXTENSION_FAIL);

export const rejectDeliveryExtension = createAction<{
    participantId: string;
    requestId: string;
}>(types.REJECT_DELIVERY_EXTENSION);

export const openLiveLinkWindow = createAction<{
    participantId: string;
    roundId?: string | null;
}>(types.OPEN_LIVE_LINK_WINDOW);
export const openLiveLinkWindowSuccess = createAction<unknown>(types.OPEN_LIVE_LINK_WINDOW_SUCCESS);
export const openLiveLinkWindowFail = createAction<IErrorType>(types.OPEN_LIVE_LINK_WINDOW_FAIL);
export const rejectDeliveryExtensionSuccess = createAction<unknown>(types.REJECT_DELIVERY_EXTENSION_SUCCESS);
export const rejectDeliveryExtensionFail = createAction<IErrorType>(types.REJECT_DELIVERY_EXTENSION_FAIL);

export const workspaceSubmissionApprovedReceived = createAction<{
    submissionId: string;
    deliverableType: string;
}>(types.WORKSPACE_SUBMISSION_APPROVED_RECEIVED);

export const workspaceLiveUrlRevisionReceived = createAction<{
    submissionId: string;
    deliverableType: string;
    feedback: string;
}>(types.WORKSPACE_LIVE_URL_REVISION_RECEIVED);

export const saveBrandReview = createAction<ISaveBrandReviewPayload>(types.SAVE_BRAND_REVIEW);
export const saveBrandReviewSuccess = createAction<ISaveBrandReviewSuccessPayload>(types.SAVE_BRAND_REVIEW_SUCCESS);
export const saveBrandReviewFail = createAction<IErrorType>(types.SAVE_BRAND_REVIEW_FAIL);

export const fetchBrandReviewsPage = createAction<{ brandId: string; page?: number; limit?: number }>(
    types.FETCH_BRAND_REVIEWS_PAGE,
);
export const fetchBrandReviewsPageSuccess = createAction<import('../../utils/brandReviewsApi').IBrandReviewsPage>(
    types.FETCH_BRAND_REVIEWS_PAGE_SUCCESS,
);
export const fetchBrandReviewsPageFail = createAction<IErrorType>(types.FETCH_BRAND_REVIEWS_PAGE_FAIL);

export const saveInfluencerReview = createAction<ISaveInfluencerReviewPayload>(types.SAVE_INFLUENCER_REVIEW);
export const saveInfluencerReviewSuccess = createAction<ISaveInfluencerReviewSuccessPayload>(
    types.SAVE_INFLUENCER_REVIEW_SUCCESS,
);
export const saveInfluencerReviewFail = createAction<IErrorType>(types.SAVE_INFLUENCER_REVIEW_FAIL);

export const manageOffsiteCampaigns = createAction<IGetCampaigns>(types.MANAGE_OFFSITE_CAMPAIGNS);
export const manageOffsiteCampaignsSuccess = createAction<IManagedCampaignsListResult>(
    types.MANAGE_OFFSITE_CAMPAIGNS_SUCCESS,
);
export const manageOffsiteCampaignsFail = createAction<IErrorType>(types.MANAGE_OFFSITE_CAMPAIGNS_FAIL);

export const updateOffsiteCampaign = createAction<IUpdateCampaignPayload>(types.UPDATE_OFFSITE_CAMPAIGN);
export const updateOffsiteCampaignSuccess = createAction<ICampaign>(types.UPDATE_OFFSITE_CAMPAIGN_SUCCESS);
export const updateOffsiteCampaignFail = createAction<IErrorType>(types.UPDATE_OFFSITE_CAMPAIGN_FAIL);

export const updateCampaignStatus = createAction<{ campaignId: string; status: 'published' | 'draft' }>(
    types.UPDATE_CAMPAIGN_STATUS,
);
export const updateCampaignStatusSuccess = createAction<void>(types.UPDATE_CAMPAIGN_STATUS_SUCCESS);
export const updateCampaignStatusFail = createAction<IErrorType>(types.UPDATE_CAMPAIGN_STATUS_FAIL);

export const deleteOffsiteCampaign = createAction<{ campaignId: string }>(types.DELETE_OFFSITE_CAMPAIGN);
export const deleteOffsiteCampaignSuccess = createAction<{ campaignId: string }>(types.DELETE_OFFSITE_CAMPAIGN_SUCCESS);
export const deleteOffsiteCampaignFail = createAction<IErrorType>(types.DELETE_OFFSITE_CAMPAIGN_FAIL);

// Invite influencer to campaign
export const inviteInfluencerToCampaign = createAction<IInviteInfluencer>(types.SEND_CAMPAIGN_MESSAGE);
export const inviteInfluencerToCampaignSuccess = createAction<IParticipant>(types.SEND_CAMPAIGN_MESSAGE_SUCCESS);
export const inviteInfluencerToCampaignFail = createAction<IErrorType>(types.SEND_CAMPAIGN_MESSAGE_FAIL);

// Brand actions
export const getBrands = createAction<any>(types.GET_BRANDS);
export const getBrandsSuccess = createAction<IBrand[]>(types.GET_BRANDS_SUCCESS);
export const getBrandsFail = createAction<IErrorType>(types.GET_BRANDS_FAIL);

export const getBrand = createAction<string>(types.GET_BRAND);
export const getBrandSuccess = createAction<IBrand>(types.GET_BRAND_SUCCESS);
export const getBrandFail = createAction<IErrorType>(types.GET_BRAND_FAIL);

export const addBrand = createAction<ICreateBrand>(types.ADD_BRAND);
export const addBrandSuccess = createAction<IBrand>(types.ADD_BRAND_SUCCESS);
export const addBrandFail = createAction<IErrorType>(types.ADD_BRAND_FAIL);

export const updateBrand = createAction<IGenericDocumentModifier>(types.UPDATE_BRAND);
export const updateBrandSuccess = createAction<any>(types.UPDATE_BRAND_SUCCESS);
export const updateBrandFail = createAction<IErrorType>(types.UPDATE_BRAND_FAIL);

export const deleteBrand = createAction<IGenericDocumentModifier>(types.DELETE_BRAND);
export const deleteBrandSuccess = createAction<any>(types.DELETE_BRAND_SUCCESS);
export const deleteBrandFail = createAction<IErrorType>(types.DELETE_BRAND_FAIL);

export const setBrandDashboardActiveBrand = createAction<IBrand | null>(types.SET_BRAND_DASHBOARD_ACTIVE_BRAND);

// Host dashboard actions
export const getHostDashboard = createAction<IGetHostDashboard>(types.GET_HOST_DASHBOARD);
export const getHostDashboardSuccess = createAction<IHostDashboardData>(types.GET_HOST_DASHBOARD_SUCCESS);
export const getHostDashboardFail = createAction<IErrorType>(types.GET_HOST_DASHBOARD_FAIL);

// Deliverable actions
export const requestDeliverableUpdate = createAction<any>(types.REQUEST_DELIEVERABLE_UPDATE);
export const requestDeliverableUpdateSuccess = createAction<any>(types.REQUEST_DELIEVERABLE_UPDATE_SUCCESS);
export const requestDeliverableUpdateFail = createAction<IErrorType>(types.REQUEST_DELIEVERABLE_UPDATE_FAIL);

export const bulkRequestRevision = createAction<{
    participantId: string;
    feedback: string;
    reasons?: string[];
    comment?: string;
}>(types.BULK_REQUEST_REVISION);
export const bulkRequestRevisionSuccess = createAction<any>(types.BULK_REQUEST_REVISION_SUCCESS);
export const bulkRequestRevisionFail = createAction<IErrorType>(types.BULK_REQUEST_REVISION_FAIL);

export const relaunchCampaign = createAction<{
    campaignId: string;
    title?: string;
    start_date: string;
    end_date: string;
}>(types.RELAUNCH_CAMPAIGN);
export const relaunchCampaignSuccess = createAction<any>(types.RELAUNCH_CAMPAIGN_SUCCESS);
export const relaunchCampaignFail = createAction<IErrorType>(types.RELAUNCH_CAMPAIGN_FAIL);

export const approveDeliverableSubmission = createAction<any>(types.APPROVE_DELIVERABLE_SUBMISSION);
export const approveDeliverableSubmissionSuccess = createAction<any>(types.APPROVE_DELIVERABLE_SUBMISSION_SUCCESS);
export const approveDeliverableSubmissionFail = createAction<IErrorType>(types.APPROVE_DELIVERABLE_SUBMISSION_FAIL);

export const uploadDeliverable = createAction<any>(types.UPLOAD_DELIVERABLE);
export const uploadDeliverableSuccess = createAction<any>(types.UPLOAD_DELIVERABLE_SUCCESS);
export const uploadDeliverableFail = createAction<IErrorType>(types.UPLOAD_DELIVERABLE_FAIL);

// Aliases kept for backward-compat with saga imports
export const sendCampaignMessage = inviteInfluencerToCampaign;
export const sendCampaignMessageSuccess = inviteInfluencerToCampaignSuccess;
export const sendCampaignMessageFail = inviteInfluencerToCampaignFail;

export const deleteCampaignMessage = createAction<any>(types.DELETE_CAMPAIGN_MESSAGE);
export const deleteCampaignMessageSuccess = createAction<any>(types.DELETE_CAMPAIGN_MESSAGE_SUCCESS);
export const deleteCampaignMessageFail = createAction<IErrorType>(types.DELETE_CAMPAIGN_MESSAGE_FAIL);

export const updateCampaignMessage = createAction<any>(types.UPDATE_CAMPAIGN_MESSAGE);
export const updateCampaignMessageSuccess = createAction<any>(types.UPDATE_CAMPAIGN_MESSAGE_SUCCESS);
export const updateCampaignMessageFail = createAction<IErrorType>(types.UPDATE_CAMPAIGN_MESSAGE_FAIL);

// Post scheduling actions
export const schedulePost = createAction<any>(types.SCHEDULE_POST);
export const schedulePostSuccess = createAction<any>(types.SCHEDULE_POST_SUCCESS);
export const schedulePostFail = createAction<IErrorType>(types.SCHEDULE_POST_FAIL);

export const getBuzzBridgeProfile = createAction<IGetBuzzBridgeProfile>(types.GET_BUZZBRIDGE_PROFILE);
export const getBuzzBridgeProfileSuccess = createAction<IBuzzBridgeProfileResponse>(
    types.GET_BUZZBRIDGE_PROFILE_SUCCESS,
);
export const getBuzzBridgeProfileFail = createAction<IErrorType>(types.GET_BUZZBRIDGE_PROFILE_FAIL);
export const getBuzzBridgeProfileNoInfluencer = createAction(types.GET_BUZZBRIDGE_PROFILE_NO_INFLUENCER);

export const getPublicInfluencerProfile = createAction<IGetPublicInfluencerProfilePayload>(
    types.GET_PUBLIC_INFLUENCER_PROFILE,
);
export const getPublicInfluencerProfileSuccess = createAction<IInfluencer>(types.GET_PUBLIC_INFLUENCER_PROFILE_SUCCESS);
export const getPublicInfluencerProfileFail = createAction<IErrorType>(types.GET_PUBLIC_INFLUENCER_PROFILE_FAIL);

export const updateBuzzBridgeProfile = createAction<IGenericDocumentModifier>(types.UPDATE_BUZZBRIDGE_PROFILE);
export const updateBuzzBridgeProfileSuccess = createAction<IInfluencer>(types.UPDATE_BUZZBRIDGE_PROFILE_SUCCESS);
export const updateBuzzBridgeProfileFail = createAction<IErrorType>(types.UPDATE_BUZZBRIDGE_PROFILE_FAIL);

export const saveManualInstagramLink = createAction<ISaveManualInstagramLinkPayload>(types.SAVE_MANUAL_INSTAGRAM_LINK);
export const saveManualInstagramLinkSuccess = createAction<{ influencerId: string }>(
    types.SAVE_MANUAL_INSTAGRAM_LINK_SUCCESS,
);
export const saveManualInstagramLinkFail = createAction<IErrorType>(types.SAVE_MANUAL_INSTAGRAM_LINK_FAIL);

// Brand social OAuth actions
export const brandSocialLogin = createAction<IBrandSocialLoginPayload>(types.BRAND_SOCIAL_LOGIN);
export const brandSocialLoginSuccess = createAction<IBrandSocialProfile>(types.BRAND_SOCIAL_LOGIN_SUCCESS);
export const brandSocialLoginFail = createAction<IErrorType>(types.BRAND_SOCIAL_LOGIN_FAIL);

export const createBrandSocialLink = createAction<ICreateBrandSocialLinkPayload>(types.CREATE_BRAND_SOCIAL_LINK);
export const updateBrandSocialLink = createAction<IUpdateBrandSocialLinkPayload>(types.UPDATE_BRAND_SOCIAL_LINK);

// Campaign Wallet actions
export const getWalletDashboard = createAction(types.GET_WALLET_DASHBOARD);
export const getWalletDashboardSuccess = createAction<IWalletDashboard>(types.GET_WALLET_DASHBOARD_SUCCESS);
export const getWalletDashboardFail = createAction<IErrorType>(types.GET_WALLET_DASHBOARD_FAIL);

export const getCommissionWallet = createAction(types.GET_COMMISSION_WALLET);
export const getCommissionWalletSuccess = createAction<ICommissionWalletSummary>(types.GET_COMMISSION_WALLET_SUCCESS);
export const getCommissionWalletFail = createAction<IErrorType>(types.GET_COMMISSION_WALLET_FAIL);

export const cashOutCommissionWallet = createAction(types.CASH_OUT_COMMISSION_WALLET);
export const cashOutCommissionWalletSuccess = createAction<ICommissionWalletSummary & { transferred_cents?: number }>(
    types.CASH_OUT_COMMISSION_WALLET_SUCCESS,
);
export const cashOutCommissionWalletFail = createAction<IErrorType>(types.CASH_OUT_COMMISSION_WALLET_FAIL);

export const fundWallet = createAction<IFundWalletPayload>(types.FUND_WALLET);
export const fundWalletSuccess = createAction<IFundWalletResponse>(types.FUND_WALLET_SUCCESS);
export const fundWalletFail = createAction<IErrorType>(types.FUND_WALLET_FAIL);

export const lockEscrow = createAction<IEscrowPayload>(types.LOCK_ESCROW);
export const lockEscrowSuccess = createAction<IEscrow>(types.LOCK_ESCROW_SUCCESS);
export const lockEscrowFail = createAction<IErrorType>(types.LOCK_ESCROW_FAIL);

export const releaseEscrow = createAction<string>(types.RELEASE_ESCROW);
export const releaseEscrowSuccess = createAction<IEscrow>(types.RELEASE_ESCROW_SUCCESS);
export const releaseEscrowFail = createAction<IErrorType>(types.RELEASE_ESCROW_FAIL);

export const payoutEscrow = createAction<IPayoutPayload>(types.PAYOUT_ESCROW);
export const payoutEscrowSuccess = createAction<IEscrow>(types.PAYOUT_ESCROW_SUCCESS);
export const payoutEscrowFail = createAction<IErrorType>(types.PAYOUT_ESCROW_FAIL);

export interface IPayoutEscrowRoundPayload {
    campaignId: string;
    participant_id: string;
    round_id: string;
}
export const payoutEscrowRound = createAction<IPayoutEscrowRoundPayload>(types.PAYOUT_ESCROW_ROUND);
export const payoutEscrowRoundSuccess = createAction(types.PAYOUT_ESCROW_ROUND_SUCCESS);
export const payoutEscrowRoundFail = createAction<IErrorType>(types.PAYOUT_ESCROW_ROUND_FAIL);

export const updateAutoReload = createAction<IUpdateAutoReloadPayload>(types.UPDATE_AUTO_RELOAD);
export const updateAutoReloadSuccess = createAction<ICampaignWallet>(types.UPDATE_AUTO_RELOAD_SUCCESS);
export const updateAutoReloadFail = createAction<IErrorType>(types.UPDATE_AUTO_RELOAD_FAIL);

/** Country resolved server-side from CDN viewer headers — no client params. */
export const getWalletFundOptions = createAction(types.GET_WALLET_FUND_OPTIONS);
export const getWalletFundOptionsSuccess = createAction<IWalletFundOptions>(types.GET_WALLET_FUND_OPTIONS_SUCCESS);
export const getWalletFundOptionsFail = createAction<IErrorType>(types.GET_WALLET_FUND_OPTIONS_FAIL);

export const getWalletTransactions = createAction<IGetTransactionsPayload>(types.GET_WALLET_TRANSACTIONS);
export const getWalletTransactionsSuccess = createAction<IWalletTransaction[]>(types.GET_WALLET_TRANSACTIONS_SUCCESS);
export const getWalletTransactionsFail = createAction<IErrorType>(types.GET_WALLET_TRANSACTIONS_FAIL);

export const verifyWalletFund = createAction<IVerifyWalletFundPayload>(types.VERIFY_WALLET_FUND);

export const campaignWalletDepositCompleted = createAction<ICampaignWalletDepositSocketPayload>(
    types.CAMPAIGN_WALLET_DEPOSIT_COMPLETED,
);

export const adminCreditCampaignWallet = createAction<IAdminCreditWalletRequest>(types.ADMIN_CREDIT_CAMPAIGN_WALLET);
export const adminCreditCampaignWalletSuccess = createAction<IAdminCreditWalletResult>(
    types.ADMIN_CREDIT_CAMPAIGN_WALLET_SUCCESS,
);
export const adminCreditCampaignWalletFail = createAction<IErrorType>(types.ADMIN_CREDIT_CAMPAIGN_WALLET_FAIL);
export const clearAdminCampaignWalletCredit = createAction(types.CLEAR_ADMIN_CAMPAIGN_WALLET_CREDIT);

export const getCampaignReport = createAction<IGetCampaignReportPayload>(types.GET_CAMPAIGN_REPORT);
export const getCampaignReportSuccess = createAction<ICampaignReportSummary>(types.GET_CAMPAIGN_REPORT_SUCCESS);
export const getCampaignReportFail = createAction<IErrorType>(types.GET_CAMPAIGN_REPORT_FAIL);

export const getCampaignReportContent = createAction<IGetCampaignReportContentPayload>(
    types.GET_CAMPAIGN_REPORT_CONTENT,
);
export const getCampaignReportContentSuccess = createAction<ICampaignReportContentResponse>(
    types.GET_CAMPAIGN_REPORT_CONTENT_SUCCESS,
);
export const getCampaignReportContentFail = createAction<IErrorType>(types.GET_CAMPAIGN_REPORT_CONTENT_FAIL);

export const getCampaignReportCreators = createAction<IGetCampaignReportCreatorsPayload>(
    types.GET_CAMPAIGN_REPORT_CREATORS,
);
export const getCampaignReportCreatorsSuccess = createAction<ICampaignReportCreatorsResponse>(
    types.GET_CAMPAIGN_REPORT_CREATORS_SUCCESS,
);
export const getCampaignReportCreatorsFail = createAction<IErrorType>(types.GET_CAMPAIGN_REPORT_CREATORS_FAIL);

export const getCampaignReportSyncStatus = createAction<{ campaignId: string; skipCache?: boolean }>(
    types.GET_CAMPAIGN_REPORT_SYNC_STATUS,
);
export const getCampaignReportSyncStatusSuccess = createAction<ICampaignReportSyncStatusResponse>(
    types.GET_CAMPAIGN_REPORT_SYNC_STATUS_SUCCESS,
);
export const getCampaignReportSyncStatusFail = createAction<IErrorType>(types.GET_CAMPAIGN_REPORT_SYNC_STATUS_FAIL);

export const syncCampaignReport = createAction<ISyncCampaignReportPayload>(types.SYNC_CAMPAIGN_REPORT);
export const syncCampaignReportSuccess = createAction<{ campaignId: string }>(types.SYNC_CAMPAIGN_REPORT_SUCCESS);
export const syncCampaignReportFail = createAction<IErrorType>(types.SYNC_CAMPAIGN_REPORT_FAIL);

export const boostCampaignReport = createAction<IBoostCampaignReportPayload>(types.BOOST_CAMPAIGN_REPORT);
export const boostCampaignReportSuccess = createAction<IBoostCampaignReportResult>(types.BOOST_CAMPAIGN_REPORT_SUCCESS);
export const boostCampaignReportFail = createAction<IErrorType>(types.BOOST_CAMPAIGN_REPORT_FAIL);

export const clearCampaignReport = createAction(types.CLEAR_CAMPAIGN_REPORT);

export const postInfluencerGuestOAuthLink = createAction<import('../../utils/influencerPrelink').GuestOAuthLinkPayload>(
    types.POST_INFLUENCER_GUEST_OAUTH_LINK,
);
export const postInfluencerGuestOAuthLinkSuccess = createAction<
    import('../../utils/influencerPrelink').InfluencerPrelinkResponse
>(types.POST_INFLUENCER_GUEST_OAUTH_LINK_SUCCESS);
export const postInfluencerGuestOAuthLinkFail = createAction<IErrorType>(types.POST_INFLUENCER_GUEST_OAUTH_LINK_FAIL);

export const postInfluencerOAuthConnect = createAction<{ code: string; provider: string }>(
    types.POST_INFLUENCER_OAUTH_CONNECT,
);
export const postInfluencerOAuthConnectSuccess = createAction(types.POST_INFLUENCER_OAUTH_CONNECT_SUCCESS);
export const postInfluencerOAuthConnectFail = createAction<IErrorType>(types.POST_INFLUENCER_OAUTH_CONNECT_FAIL);

export const postInfluencerFromConnectedInstagram = createAction(types.POST_INFLUENCER_FROM_CONNECTED_INSTAGRAM);
export const postInfluencerFromConnectedInstagramSuccess = createAction(
    types.POST_INFLUENCER_FROM_CONNECTED_INSTAGRAM_SUCCESS,
);
export const postInfluencerFromConnectedInstagramFail = createAction<IErrorType>(
    types.POST_INFLUENCER_FROM_CONNECTED_INSTAGRAM_FAIL,
);

export const claimPendingInfluencer = createAction(types.CLAIM_PENDING_INFLUENCER);
export const claimPendingInfluencerSuccess = createAction<boolean>(types.CLAIM_PENDING_INFLUENCER_SUCCESS);
export const claimPendingInfluencerFail = createAction<IErrorType>(types.CLAIM_PENDING_INFLUENCER_FAIL);
